import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { SubscriptionAllDetails } from './subscriptionType';
import SubscriptionApi from './subscriptionApi';
import { formatTimestamp } from '../lib/global_function';
import { PlusCircle, Trash, ArrowRightCircle, ArrowUpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';
import { FORMATTED_MONTHLY_FEE, MONTHLY_FEE } from '../components/common/constants/constants';

const SubscriptionPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Account', path: '/main/account' },
    { label: 'Subscription', path: '/main/account/subscription' },
  ];

  const [subscriptionData, setSubscriptionData] = useState<SubscriptionAllDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await SubscriptionApi.getSubscriptionAndAll();
        setSubscriptionData(response);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(`${error.message}`);
        } else {
          setErrorMessage('Failed to fetch subscriptions. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  const handleMakePayment = () => {
    navigate('/main/account/subscription/make_payment', { 
      state: { subscription: subscriptionData?.subscription } 
    });
  };

  const handleCancel = (paymentIntentId: string) => {
    navigate('/main/account/subscription/refund_payment', { 
      state: { payment_intent_id: paymentIntentId } 
    });
  };

  // Helper function to calculate net amount for a payment_intent_id
  const getNetAmountForPaymentIntent = (paymentIntentId: string): number => {
    if (!subscriptionData?.subscription_payments) return 0;

    const paymentsForIntent = subscriptionData.subscription_payments.filter(
      payment => payment.gateway_payment_intent_id === paymentIntentId
    );

    const netAmount = paymentsForIntent.reduce((sum, payment) => {
      return sum + Number(payment.amount);
    }, 0);

    return netAmount;
  };

  // Helper function to determine if Cancel button should be shown
  const shouldShowCancelButton = (payment: any): boolean => {
    // Must be a CHARGE transaction
    if (payment.transaction_type !== 'CHARGE') return false;
    
    // Must have a payment_intent_id
    if (!payment.gateway_payment_intent_id) return false;

    // Subscription must be ACTIVE and STANDARD
    const currentSubscription = subscriptionData?.subscription;
    if (!currentSubscription || 
        currentSubscription.status !== 'ACTIVE' || 
        currentSubscription.plan_name !== 'STANDARD') {
      return false;
    }

    // Net amount for this payment_intent_id must be > 0
    const netAmount = getNetAmountForPaymentIntent(payment.gateway_payment_intent_id);
    return netAmount > 0;
  };

  const renderActionSection = () => {
    const currentSubscription = subscriptionData?.subscription;

    if (!currentSubscription) {
      return (
        <div className="mb-6">
          <Alert variant="error" className="mb-4">
            You don't currently have any subscriptions. Start using our services for free by clicking the button below.
          </Alert>
          <Button onClick={handleMakePayment} size="medium" variant="default">
            <PlusCircle className="size-5" />
            Start Subscription
          </Button>
        </div>
      );
    }

    if (currentSubscription.status !== 'ACTIVE') {
      return (
        <div className="mb-6">
          <Alert variant="error" className="mb-4">
            Your subscription status is {currentSubscription.status}. Start it now to continue using our services.
          </Alert>
          <Button onClick={handleMakePayment} size="medium" variant="default">
            <PlusCircle className="size-5" />
            Start Subscription
          </Button>
        </div>
      );
    }

    // Active subscription - check plan type
    if (currentSubscription.status === 'ACTIVE') {
      if (currentSubscription.plan_name === 'FREE') {
        return (
          <div className="mb-6">
            <p className="text-base text-foreground mb-4">
              You're currently on our <strong>Free plan</strong> with limited access to MrJoe Pro services. 
              Upgrade to our <strong>Standard plan</strong> for unlimited features at just <strong>{FORMATTED_MONTHLY_FEE}/Month</strong> with no contract required.
            </p>
            <Button onClick={handleMakePayment} size="medium" variant="default">
              <ArrowUpCircle className="size-5" />
              Start Paid Subscription
            </Button>
          </div>
        );
      }

      if (currentSubscription.plan_name === 'STANDARD') {
        return (
          <div className="mb-6">
            <p className="text-base text-foreground mb-4">
              Your <strong>Standard plan</strong> subscription is active. Click below to extend your subscription 
              and continue enjoying unlimited access to all MrJoe Pro features.
            </p>
            <Button onClick={handleMakePayment} size="medium" variant="default">
              <ArrowRightCircle className="size-5" />
              Extend Paid Subscription
            </Button>
          </div>
        );
      }
    }

    return null;
  };

  if (loading) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  const currentSubscription = subscriptionData?.subscription;
  const payments = subscriptionData?.subscription_payments || [];

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Subscriptions</h2>

        {errorMessage ? (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        ) : (
          <>
            {renderActionSection()}
            
            {currentSubscription && (
              <>
                <div className="overflow-x-auto border border-gray-200 rounded-lg mt-5">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          Plan
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          Start Date
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          End Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-paragraph">
                          {currentSubscription.status}
                        </td>
                        <td className="px-4 py-3 text-paragraph">
                          {currentSubscription.plan_name}
                        </td>
                        <td className="px-4 py-3 text-paragraph">
                          {formatTimestamp(currentSubscription.current_period_start)}
                        </td>
                        <td className="px-4 py-3 text-paragraph">
                          {currentSubscription.current_period_end
                            ? formatTimestamp(currentSubscription.current_period_end)
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

{payments.length > 0 && (
  <div className="mt-8">
    <h3 className="text-xl font-bold text-foreground mb-4">Payment History</h3>
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Transaction Type
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Amount
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Months
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Transaction Date
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Payment Intent ID
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Refund ID
            </th>
            <th className="px-4 py-3 text-left font-semibold text-foreground">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {payments.map((payment, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-paragraph">
                {payment.transaction_type}
              </td>
              <td className="px-4 py-3 text-paragraph">
                ${Number(payment.amount).toFixed(2)}
              </td>
              <td className="px-4 py-3 text-paragraph">
                {payment.number_months 
                  ? `${Math.abs(payment.number_months)} ${payment.transaction_type === 'CHARGE' ? '(Paid)' : '(Refunded)'}`
                  : 'N/A'}
              </td>
              <td className="px-4 py-3 text-paragraph">
                {formatTimestamp(payment.transaction_date)}
              </td>
              <td className="px-4 py-3 text-paragraph">
                {payment.gateway_payment_intent_id || 'N/A'}
              </td>
              <td className="px-4 py-3 text-paragraph">
                {payment.gateway_refund_id || 'N/A'}
              </td>
              <td className="px-4 py-3">
                {shouldShowCancelButton(payment) && (
                  <Button
                    onClick={() => handleCancel(payment.gateway_payment_intent_id!)}
                    size="medium"
                    variant="default"
                    title="Cancel and Refund"
                  >
                    <Trash className="size-5" />
                    Cancel
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-100 border-t-2 border-gray-300">
          <tr>
            <td className="px-4 py-3 text-left font-bold text-foreground">
              Total
            </td>
            <td className="px-4 py-3 font-bold text-foreground">
              ${payments.reduce((sum, payment) => sum + Number(payment.amount), 0).toFixed(2)}
            </td>
            <td className="px-4 py-3 font-bold text-foreground">
              {payments.reduce((sum, payment) => sum + (payment.number_months || 0), 0)} months
            </td>
            <td className="px-4 py-3" colSpan={4}>
              {/* Empty cells for alignment */}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
)}

              </>
            )}
          </>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default SubscriptionPage;